import { encodeSupportTicketFilterGroup } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import {
  isDefaultView,
  withSpamTagExclusion,
} from "@redotech/redo-support-v2/query/default-views";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useFilterGroup,
  useHasActiveFilters,
  useResetFilters,
} from "../filters/filters-context";
import { useSupportV2FolderActions } from "./use-support-v2-folder-actions";
import { useSupportV2ViewsAndFolders } from "./use-support-v2-views-and-folders";

export function buildViewUrl(
  teamId: string,
  viewId: string,
  filterGroup: unknown,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("view", viewId);
  const effectiveFilterGroup = withSpamTagExclusion(
    (filterGroup as FilterGroup | null) ?? null,
  );
  try {
    const encoded = encodeSupportTicketFilterGroup(effectiveFilterGroup);
    params.set("fg", encoded);
  } catch {
    // ignore encoding errors
  }
  return `${base}?${params.toString()}`;
}

export function useViewManagement() {
  const team = useRequiredContext(TeamContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentFilterGroup = useFilterGroup();
  const resetFilters = useResetFilters();
  const hasActiveFilters = useHasActiveFilters();
  const { views } = useSupportV2ViewsAndFolders();
  const { createView, updateView, deleteView } = useSupportV2FolderActions();

  const activeViewId = searchParams.get("view");
  const activeDefaultViewParam = searchParams.get("defaultView");
  const activeDefaultView =
    activeDefaultViewParam && isDefaultView(activeDefaultViewParam)
      ? activeDefaultViewParam
      : null;

  const activeView = useMemo(() => {
    if (!activeViewId) {
      return null;
    }
    return views.find((v) => String(v.id) === activeViewId) ?? null;
  }, [activeViewId, views]);

  const filtersMatchView = useMemo(() => {
    if (!activeView) {
      return false;
    }
    const currentFg = searchParams.get("fg") ?? "";
    let viewFg = "";
    try {
      viewFg = encodeSupportTicketFilterGroup(
        withSpamTagExclusion(
          (activeView.filterGroup as FilterGroup | null) ?? null,
        ),
      );
    } catch {
      viewFg = "";
    }
    return currentFg === viewFg;
  }, [activeView, searchParams]);

  const reservedViewNames = useMemo(
    () => new Set(views.map((v) => v.name.toLowerCase())),
    [views],
  );

  const reservedViewNamesExcludingCurrent = useMemo(
    () =>
      new Set(
        [...reservedViewNames].filter(
          (n) => !activeView || n !== activeView.name.toLowerCase(),
        ),
      ),
    [reservedViewNames, activeView],
  );

  const handleCreateView = useHandler(async (name: string) => {
    const viewId = await createView(name, currentFilterGroup, null);
    navigate(buildViewUrl(team._id, viewId, currentFilterGroup));
  });

  const handleUpdateViewFilters = useHandler(async () => {
    if (!activeView) {
      return;
    }
    await updateView(
      String(activeView.id),
      activeView.name,
      currentFilterGroup,
    );
  });

  const handleEditViewName = useHandler(async (name: string) => {
    if (!activeView) {
      return;
    }
    await updateView(
      String(activeView.id),
      name,
      (activeView.filterGroup as FilterGroup | null) ?? null,
    );
  });

  const handleDeleteView = useHandler(async () => {
    if (!activeView) {
      return;
    }
    await deleteView(String(activeView.id));
    navigate(`/stores/${team._id}/support-v2/`);
  });

  const handleCancel = useHandler(() => {
    resetFilters();
    navigate(`/stores/${team._id}/support-v2/`);
  });

  return {
    activeView,
    activeDefaultView,
    filtersMatchView,
    hasActiveFilters,
    reservedViewNames,
    reservedViewNamesExcludingCurrent,
    handleCreateView,
    handleUpdateViewFilters,
    handleEditViewName,
    handleDeleteView,
    handleCancel,
  };
}
